import { createTheme, type PaletteMode } from '@mui/material';
import type { PaletteOptions } from '@mui/material/styles';
// More info: https://github.com/mui/mui-x/issues/3967#issuecomment-1041337663
// noinspection ES6UnusedImports
import '@mui/x-data-grid-pro/themeAugmentation';
import '@mui/x-date-pickers/themeAugmentation';
import { colors } from './colors';
import { globalTokens } from './globalTokens';
import { darkPalette } from './palette.dark';
import { lightPalette } from './palette.light';
import { typography } from './typography';
import { zIndex } from './zIndex';

export const createCustomTheme = (mode: PaletteMode) => {
  const theme = createTheme();
  const palette = mode === 'light' ? lightPalette : darkPalette;
  const paletteOptions = {
    mode,
    ...colors,
    ...palette,
  } as PaletteOptions;

  return createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            boxShadow: 'none',
            borderRadius: theme.spacing(1),
            padding: 24,
          },
          container: {
            alignItems: 'flex-start',
          },
        },
        defaultProps: {
          maxWidth: 'md',
          slotProps: {
            paper: {
              sx: {
                mt: 10,
              },
            },
            backdrop: {
              sx: {
                backdropFilter: 'blur(4px)',
                backgroundColor: mode === 'dark' ? '#00000066' : '#00000033',
              },
            },
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: 0,
            paddingBottom: 16,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: 0,
            paddingBottom: 16,
            paddingTop: 8,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            height: '52px',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: palette.background?.paper,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            color: palette.text?.primary,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            height: '100%',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: palette.custom.inputLabelColor,
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            marginTop: 0,
          },
        },
      },
      MuiChip: {
        variants: [
          {
            props: { variant: 'datagrid' },
            style: {
              borderRadius: theme.spacing(0.5),
              fontWeight: '700',
              fontSize: '10px',
              textTransform: 'uppercase',
            },
          },
          {
            props: { variant: 'datagridOutlined' },
            style: {
              borderRadius: theme.spacing(0.5),
              fontWeight: '700',
              fontSize: '11px',
              backgroundColor: 'transparent',
              border: '1px solid',
              borderColor: palette.divider,
            },
          },
        ],
      },
      MuiButton: {
        variants: [
          {
            props: { color: 'offset' },
            style: {
              backgroundColor: mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[300],
            },
          },
        ],
      },
      MuiPaper: {
        variants: [
          {
            props: { variant: 'wrapper' },
            style: {
              boxShadow: theme.shadows[0],
              padding: theme.spacing(2),
              backgroundColor: palette.background?.paper,
              borderRadius: theme.spacing(1),
            },
          },
          {
            props: { variant: 'gray' },
            style: {
              padding: theme.spacing(2),
              borderRadius: theme.spacing(1),
              boxShadow: 'none',
              backgroundColor: palette.background?.default,
            },
          },
        ],
      },

      MuiDataGrid: {
        defaultProps: {
          showToolbar: true,
          pagination: true,
          rowHeight: 38,
          columnHeaderHeight: 32,
          disableColumnResize: true,
        },
        styleOverrides: {
          root: {
            border: 'none',
            backgroundColor: 'transparent',
            color: palette.text?.primary,
          },
          columnHeaders: {
            color: palette.text?.primary,
          },
          cell: {
            borderColor: palette.divider,
          },
          row: {
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? colors.emerald[900] : '#00a76f42',
              '&:hover': {
                backgroundColor: mode === 'dark' ? colors.emerald[800] : '#00a76f6e',
              },
            },
            '&:hover': {
              backgroundColor: palette.action?.hover,
            },
            // LeafDestinationPickerDialog — occupied (current location) rows
            '&.row-occupied, &.row-occupied:hover': {
              backgroundColor:
                mode === 'dark' ? 'rgba(255,255,255,0.04) !important' : 'rgba(0,0,0,0.07) !important',
              borderLeft: `3px solid ${palette.divider}`,
              cursor: 'not-allowed',
            },
            '&.row-occupied .MuiDataGrid-cell': {
              color: `${palette.text?.disabled} !important`,
              opacity: 0.6,
              pointerEvents: 'none',
            },
            // LeafDestinationPickerDialog — selected destination row
            '&.row-selected, &.row-selected:hover': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(0,167,111,0.13) !important'
                  : 'rgba(0,167,111,0.13) !important',
              outline: `2px solid ${mode === 'dark' ? colors.emerald[400] : colors.emerald[600]}`,
              outlineOffset: '-2px',
            },
          },
          footerContainer: {
            backgroundColor: palette.background?.paper,
            borderTop: `1px solid ${palette.divider}`,
          },
          columnSeparator: {
            color: palette.divider,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            wordWrap: 'break-word',
            borderColor: palette.divider,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: palette.text?.primary,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: theme.spacing(1),
          },
        },
      },
      MuiDatePicker: {
        defaultProps: {
          slotProps: {
            textField: {
              size: 'small',
            },
          },
        },
      },
    },
    palette: {
      ...paletteOptions,
    },

    typography,
    zIndex,
    globalTokens,
  });
};
